import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import MediaIcon from '~/assets/icons/MediaIcon';
import { PhotoIcon, DocumentIcon, VideoCameraIcon, MicrophoneIcon } from '@heroicons/react/24/outline';
import { useGetMediaMessages, MediaMessage } from '~/services/customer';

const CustomerDocuments = () => {
  const [t] = useTranslation('customer');
  const { customerId, agentId } = useParams();
  const [mediaMessages, setMediaMessages] = useState<MediaMessage[]>([]);
  const [getMediaMessages, { loading, error }] = useGetMediaMessages();

  useEffect(() => {
    if (customerId && agentId) {
      getMediaMessages({
        variables: {
          data: {
            agent_id: agentId,
            person_id: customerId
          }
        }
      }).then((result) => {
        if (result.data?.get_media_messages?.hasError === false) {
          setMediaMessages(result.data.get_media_messages.data || []);
        }
      });
    }
  }, [customerId, agentId, getMediaMessages]);

  // Group media messages by type for statistics
  const getMediaStats = () => {
    const stats = {
      images: 0,
      documents: 0,
      videos: 0,
      audios: 0
    };

    mediaMessages.forEach((message) => {
      const mediaType = message.media?.type?.toLowerCase();
      if (mediaType?.includes('image')) {
        stats.images++;
      } else if (mediaType?.includes('video')) {
        stats.videos++;
      } else if (mediaType?.includes('audio')) {
        stats.audios++;
      } else {
        stats.documents++;
      }
    });

    return stats;
  };

  const stats = getMediaStats();

  const getFileIcon = (mediaType: string) => {
    const type = mediaType?.toLowerCase();
    if (type?.includes('image')) {
      return <PhotoIcon className="h-8 w-8 text-blue-500" />;
    } else if (type?.includes('video')) {
      return <VideoCameraIcon className="h-8 w-8 text-green-500" />;
    } else if (type?.includes('audio')) {
      return <MicrophoneIcon className="h-8 w-8 text-yellow-500" />;
    } else {
      return <DocumentIcon className="h-8 w-8 text-red-500" />;
    }
  };

  const getFileTypeLabel = (mediaType: string) => {
    const type = mediaType?.toLowerCase();
    if (type?.includes('image')) {
      return t('image', 'Image');
    } else if (type?.includes('video')) {
      return t('video', 'Video');
    } else if (type?.includes('audio')) {
      return t('audio', 'Audio');
    } else {
      return t('document', 'Document');
    }
  };

  const getFileName = (mediaMessage: MediaMessage) => {
    return mediaMessage.media?.media_id || 'Unknown file';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Documents Header */}
      <div className="flex items-center gap-4">
        <MediaIcon isActive={true} />
        <div>
          <h3 className="text-xl font-semibold">{t('customer_media', 'Documentos de Clientes')}</h3>
          <p className="text-base-content/70">{t('media_description', 'Files and media associated with this customer')}</p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Column 1: Statistics and Upload */}
        <div className="space-y-6">
          {/* Documents Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="stat bg-base-100 border-base-300 border shadow-sm">
              <div className="stat-figure text-primary">
                <PhotoIcon className="h-8 w-8" />
              </div>
              <div className="stat-title">{t('total_images', 'Total Images')}</div>
              <div className="stat-value text-primary">{stats.images}</div>
            </div>
            <div className="stat bg-base-100 border-base-300 border shadow-sm">
              <div className="stat-figure text-secondary">
                <DocumentIcon className="h-8 w-8" />
              </div>
              <div className="stat-title">{t('total_documents', 'Total Documents')}</div>
              <div className="stat-value text-secondary">{stats.documents}</div>
            </div>
            <div className="stat bg-base-100 border-base-300 border shadow-sm">
              <div className="stat-figure text-accent">
                <VideoCameraIcon className="h-8 w-8" />
              </div>
              <div className="stat-title">{t('total_videos', 'Total Videos')}</div>
              <div className="stat-value text-accent">{stats.videos}</div>
            </div>
            <div className="stat bg-base-100 border-base-300 border shadow-sm">
              <div className="stat-figure text-success">
                <MicrophoneIcon className="h-8 w-8" />
              </div>
              <div className="stat-title">{t('total_audios', 'Total de audios')}</div>
              <div className="stat-value text-success">{stats.audios}</div>
            </div>
          </div>

          {/* Upload New File */}
          <div className="card bg-base-100 border-base-300 border shadow-sm">
            <div className="card-body">
              <h4 className="card-title text-lg">{t('upload_new_file', 'Subir Nuevo Archivo')}</h4>
              <div className="border-2 border-dashed border-base-300 rounded-lg p-6 text-center">
                <MediaIcon isActive={false} className="mx-auto mb-4 h-12 w-12 text-base-content/50" />
                <p className="text-base-content/70 mb-4">
                  {t('drag_drop_files', 'Drag and drop files here, or click to select files')}
                </p>
                <button className="btn btn-primary">
                  {t('select_files', 'Select Files')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Column 2: Files List */}
        <div>
          <div className="card bg-base-100 border-base-300 border shadow-sm h-full">
            <div className="card-body">
              <h4 className="card-title text-lg">{t('files_list', 'Lista de Archivos')}</h4>

              {loading ? (
                <div className="text-center py-8">
                  <div className="loading loading-spinner loading-lg"></div>
                  <p className="text-base-content/70 mt-4">{t('loading_media', 'Loading media files...')}</p>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-error">{t('error_loading_media', 'Error loading media files')}</p>
                </div>
              ) : mediaMessages.length === 0 ? (
                <div className="text-center py-8">
                  <MediaIcon isActive={false} className="mx-auto mb-4 h-16 w-16 text-base-content/30" />
                  <p className="text-base-content/70">{t('no_media_files', 'No media files found for this customer.')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {mediaMessages.map((message) => (
                    <div key={message._id} className="flex items-center justify-between p-4 border border-base-300 rounded-lg hover:bg-base-200/50 transition-colors">
                      <div className="flex items-center gap-4">
                        {getFileIcon(message.media?.type || '')}
                        <div>
                          <h5 className="font-medium text-base-content">{getFileName(message)}</h5>
                          <p className="text-sm text-base-content/70">
                            {getFileTypeLabel(message.media?.type || '')} • {t('uploaded', 'Uploaded')} {formatDate(message.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          className="btn btn-ghost btn-sm"
                          onClick={() => window.open(message.media?.url, '_blank')}
                        >
                          {t('view', 'View')}
                        </button>
                        <button 
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = message.media?.url || '';
                            link.download = getFileName(message);
                            link.click();
                          }}
                        >
                          {t('download', 'Download')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDocuments;
